import { useTranslation } from "react-i18next"
import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { VList, VListHandle } from "virtua"
import { LocateFixed, MessageSquare, CheckCheck } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import Loader from "@/components/Loader"
import { SearchHit } from "@/lib/searchResultParser"
import { XmppContext } from "@/stream/xmppClient"
import useSearchHistory from "@/hooks/useSearchHistory"
import { HistoryDirection } from "@/api/searchHistory"
import { ArchivedChatContext } from "@/contexts/ArchivedChatContext"
import { useAppSelector } from "@/reducers/hooks"
import ImageGallery from "@/components/photoView/ImageGallery"
import PDFViewer from "@/components/pdfView/PDFViewer"
import SearchHistoryMessage from "./SearchHistoryMessage"

export default function SearchMessageDetails({
  clickedMessageHit,
  mediaSource,
  portalContainer,
}: {
  clickedMessageHit: SearchHit
  mediaSource: string
  portalContainer?: HTMLElement
}) {
  const { t } = useTranslation()
  const { client } = useContext(XmppContext)
  const { state, messages, loadMore, retry } = useSearchHistory(
    client,
    clickedMessageHit,
  )
  const gallery = useAppSelector((state) => state.overlays.imageGallery)
  const pdf = useAppSelector((state) => state.overlays.PDFViewer)
  const list = useRef<VListHandle>(null)
  const positioned = useRef(false)
  const userScrolled = useRef(false)
  const [showReturn, setShowReturn] = useState(false)
  const anchorIndex = messages.findIndex(
    (message) => message.id === state?.anchorMessageId,
  )
  const archiveMessages = state?.messages[clickedMessageHit.document.chat_jid]
  const context = useMemo(
    () =>
      archiveMessages
        ? {
            chatJid: clickedMessageHit.document.chat_jid,
            chatMessages: archiveMessages,
            mediaSource,
          }
        : undefined,
    [archiveMessages, clickedMessageHit.document.chat_jid, mediaSource],
  )

  useLayoutEffect(() => {
    if (!state || state.error || positioned.current || anchorIndex < 0) return
    list.current?.scrollToIndex(anchorIndex + 1, { align: "center" })
    positioned.current = true
  }, [state, anchorIndex])

  const onScroll = useCallback(() => {
    const viewport = list.current
    if (!viewport || !positioned.current || !state) return
    setShowReturn(
      anchorIndex + 1 < viewport.findStartIndex() ||
        anchorIndex + 1 > viewport.findEndIndex(),
    )
    if (state.loading || !userScrolled.current) return
    if (
      viewport.scrollOffset < 180 &&
      state.older.hasMore &&
      !state.older.error
    )
      void loadMore("older")
    else if (
      viewport.scrollSize - viewport.viewportSize - viewport.scrollOffset <
        180 &&
      state.newer.hasMore &&
      !state.newer.error
    )
      void loadMore("newer")
  }, [loadMore, state, anchorIndex])

  useEffect(() => {
    if (!state || state.loading || !userScrolled.current) return
    const frame = requestAnimationFrame(onScroll)
    return () => cancelAnimationFrame(frame)
  }, [state, onScroll])

  if (!state)
    return (
      <div
        role="status"
        aria-label={t("app.search.loadingConversation")}
        className="flex flex-1 items-center justify-center p-8"
      >
        <Loader />
      </div>
    )
  if (state.error)
    return (
      <div role="alert" className="p-8 text-center">
        <p>{state.error}</p>
        {state.errorKind !== "permission" && (
          <Button
            onClick={retry}
            className="mt-4 bg-blue-600 text-white hover:bg-blue-700"
          >
            {t("app.actions.tryAgain")}
          </Button>
        )}
      </div>
    )

  const boundary = (direction: HistoryDirection) => (
    <div
      key={direction}
      className="mx-auto flex min-h-14 w-full max-w-3xl items-center justify-center px-4 py-3 text-xs text-muted-foreground"
    >
      {state[direction].error ? (
        <div role="alert">
          {state[direction].error}{" "}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void loadMore(direction)}
          >
            {t("app.actions.tryAgain")}
          </Button>
        </div>
      ) : state.loading === direction ? (
        <div
          role="status"
          aria-label={
            direction === "older"
              ? t("app.search.loadingOlder")
              : t("app.search.loadingNewer")
          }
        >
          <Loader small />
        </div>
      ) : state[direction].hasMore ? (
        <Button
          variant="ghost"
          size="sm"
          disabled={!!state.loading}
          onClick={() => void loadMore(direction)}
        >
          {direction === "older"
            ? t("app.search.loadOlder")
            : t("app.search.loadNewer")}
        </Button>
      ) : (
        <div className="flex w-full items-center gap-3">
          <Separator className="flex-1" />
          <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-muted/50 px-3 py-1">
            {direction === "older" ? (
              <MessageSquare className="size-3.5" />
            ) : (
              <CheckCheck className="size-3.5" />
            )}
            {direction === "older"
              ? t("app.search.startOfConversation")
              : t("app.search.endOfConversation")}
          </span>
          <Separator className="flex-1" />
        </div>
      )}
    </div>
  )

  return (
    <ArchivedChatContext.Provider value={context}>
      <div
        className="relative flex min-h-0 w-full flex-1 flex-col bg-background"
        data-testid="search-history"
        onWheelCapture={() => {
          userScrolled.current = true
          onScroll()
        }}
        onTouchMoveCapture={() => {
          userScrolled.current = true
          onScroll()
        }}
        onPointerDownCapture={() => {
          userScrolled.current = true
        }}
        onKeyDownCapture={(event) => {
          if (
            [
              "ArrowUp",
              "ArrowDown",
              "PageUp",
              "PageDown",
              "Home",
              "End",
              " ",
            ].includes(event.key)
          )
            userScrolled.current = true
        }}
      >
        <VList
          ref={list}
          shift={state.shift}
          onScroll={onScroll}
          tabIndex={0}
          className="min-h-0 flex-1"
          style={{ overflowAnchor: "none" }}
        >
          {boundary("older")}
          {messages.map((message, index) => (
            <SearchHistoryMessage
              key={message.id}
              message={message}
              previous={messages[index - 1]}
              next={messages[index + 1]}
              highlighted={index === anchorIndex}
            />
          ))}
          {boundary("newer")}
        </VList>
        {showReturn && (
          <Button
            variant="floating"
            className="absolute bottom-4 left-1/2 z-10 -translate-x-1/2"
            data-testid="return-to-search-message"
            onClick={() => {
              userScrolled.current = false
              list.current?.scrollToIndex(anchorIndex + 1, { align: "center" })
              setShowReturn(false)
            }}
          >
            <LocateFixed className="size-4" />
            {t("app.search.backToMatched")}
          </Button>
        )}
      </div>
      {gallery.visible && gallery.externalSource === mediaSource && (
        <ImageGallery messages={messages} portalContainer={portalContainer} />
      )}
      {pdf.visible && pdf.externalSource === mediaSource && (
        <PDFViewer portalContainer={portalContainer} />
      )}
    </ArchivedChatContext.Provider>
  )
}
